/**
 * Canonical design tokens for the generic table component.
 *
 * These are intentionally plain values (CSS custom properties) so the table
 * components remain theme-agnostic and do not duplicate MUI palette strings.
 */
export const tableTokens = {
  text: {
    disabled: 'var(--mui-palette-text-disabled)',
    secondary: 'var(--mui-palette-text-secondary)',
  },
  link: {
    color: 'var(--mui-palette-primary-main)',
  },
  status: {
    success: 'success',
    warning: 'warning',
    error: 'error',
    info: 'info',
    default: 'default',
  },
  chip: {
    background: (color: string) => `var(--mui-palette-${color}-light)`,
    foreground: (color: string) => `var(--mui-palette-${color}-dark)`,
  },

  // ── Pinned-layer backgrounds (UX-TABLE-002) ──────────────────────────
  pinned: {
    headerBg: 'background.paper',
    headerNormalBg: 'background.paper',
    bodyBg: 'background.paper',
    bodyHoverBg: 'action.hover',
    bodySelectedBg: 'action.selected',
    filterBg: 'background.paper',
    aggregateBg: 'action.hover',
    shadowLeft: '4px 0 8px -4px rgba(0,0,0,0.2)',
    shadowRight: '-4px 0 8px -4px rgba(0,0,0,0.2)',
  },

  // ── Layer z-indexes ───────────────────────────────────────────────────
  zIndex: {
    header: 10,
    pinnedHeader: 20,
    body: 1,
    pinnedBody: 20,
    filterRow: 20,
    aggregate: 15,
    resizeHandle: 30,
    dragOverlay: 40,
  },

  // ── Heights by density ────────────────────────────────────────────────
  density: {
    compact: {
      headerHeight: 40,
      rowHeight: 36,
      filterRowHeight: 40,
      cellPaddingY: 4,
      cellPaddingX: 8,
    },
    standard: {
      headerHeight: 48,
      rowHeight: 48,
      filterRowHeight: 48,
      cellPaddingY: 6,
      cellPaddingX: 12,
    },
    comfortable: {
      headerHeight: 56,
      rowHeight: 64,
      filterRowHeight: 56,
      cellPaddingY: 10,
      cellPaddingX: 16,
    },
  } as Record<string, {
    headerHeight: number;
    rowHeight: number;
    filterRowHeight: number;
    cellPaddingY: number;
    cellPaddingX: number;
  }>,

  // ── Action column ─────────────────────────────────────────────────────
  actionColumn: {
    minWidth: 96,
    width: 120,
    stickyRight: true,
  },

  // ── Toolbar spacing ───────────────────────────────────────────────────
  toolbar: {
    paddingY: 12,
    paddingX: 16,
    gap: 12,
    titleMinWidth: 200,
    overflowThreshold: 600,
  },

  // ── Focus ring ────────────────────────────────────────────────────────
  focusRing: {
    outline: '2px solid var(--mui-palette-primary-main)',
    outlineOffset: -2,
  },

  // ── Typography ────────────────────────────────────────────────────────
  typography: {
    headerFontSize: '0.875rem',
    headerFontWeight: 600,
    bodyFontSize: '0.875rem',
    captionFontSize: '0.75rem',
  },

  // ── Responsive breakpoints ────────────────────────────────────────────
  breakpoints: {
    mobile: 360,
    tablet: 768,
    desktop: 1280,
    wide: 1600,
  },
} as const;
